import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import AppStoreBadge from '../components/AppStoreBadge';
import GooglePlayBadge from '../components/GooglePlayBadge';
import { isNativeApp } from '../utils/isNativeApp';
import { fireConversion } from '../utils/marketingTag';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── 🔑 THE RULES ARE THE SERVER'S (Check Eligibility fix 3, push C — backend constants/quickCheckRules) ──────────
//
// This page kept its own copy of every rule: the states, "single-family only", "above 560", a $3,000,000 ceiling
// (the LOAN maximum typed as a price) and "10–15%" down. All of it now comes from GET /api/uw-matrix/public/quick-check
// (the options, the bounds and the copy) and POST /api/uw-matrix/public/quick-check/estimate (the answer), which read
// backend/constants/afmMatrix.js — the same matrix the decision reads. backend/test/prequalSharedRules.test.js fails if
// a threshold, a state or a rule's copy is typed here again.
//
// ⚠️ AN ESTIMATE, NEVER AN APPROVAL. The answer is "estimate_fit" or "not_a_fit"; the copy says so.
// ⚠️ NO RULES, NO CHECK. If the rules cannot be loaded the page says so and offers a retry — it never falls back to
// a copy of its own.

// ── Screen IDs in flow order ─────────────────────────────────────────────────
const FLOW = [
  'welcome',
  'q1_state',
  'q2_primary_residence',
  'q3_property_type',
  'q4_purchase_price',
  'q5_down_payment',
  'q6_credit_score',
  'q7_agent',
  'q8_stage',
];

// Question screens used for progress bar (excludes welcome)
const QUESTION_SCREENS = FLOW.slice(1);

// ── Doorly confetti colors ───────────────────────────────────────────────────
const CONFETTI_COLORS = ['#fffd5b', '#3a6ad9', '#ffffff', '#fffd5b', '#3a6ad9'];

// ── All 50 US States ─────────────────────────────────────────────────────────
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

// /register's credit answer (routes/auth.js stores '560+' / 'Unsure'): any range at or above the published floor, or
// "Not sure". The ranges themselves are the server's; this maps their codes, it holds no threshold.
const legacyCreditCode = (code) => (String(code || '').startsWith('fico_') ? 'above_560' : code === 'unsure' ? 'unsure' : '');

function PreQualification() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('welcome');
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('forward');
  const [answers, setAnswers] = useState({});
  const [purchasePrice, setPurchasePrice] = useState('');
  const [priceError, setPriceError] = useState('');
  const [stateSelection, setStateSelection] = useState('');
  const [stateError, setStateError] = useState('');
  const [agentPopup, setAgentPopup] = useState(null); // 'yes' | 'no' | null
  const [priceRangeModal, setPriceRangeModal] = useState(null); // { heading, body } from the server | null
  const [stageModal, setStageModal] = useState(null); // 'home_picked' | 'under_contract' | null
  const [waitlistModal, setWaitlistModal] = useState(null); // null | { state, stateName }
  const [waitlistForm, setWaitlistForm] = useState({ firstName: '', email: '', phone: '' });
  const [waitlistErrors, setWaitlistErrors] = useState({});
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistApiError, setWaitlistApiError] = useState('');
  const [rejection, setRejection] = useState(null); // { heading, body } — the server's copy
  const [history, setHistory] = useState(['welcome']);
  const containerRef = useRef(null);
  // 🔑 The rules (options, bounds, copy) and the latest estimate — both from the server (see the note at the top).
  const [rules, setRules] = useState(null);
  const [rulesError, setRulesError] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [checkError, setCheckError] = useState('');
  const [checking, setChecking] = useState(false);

  const loadRules = React.useCallback(async () => {
  setRulesError(false);
  try {
  const res = await fetch(`${API}/api/uw-matrix/public/quick-check`);
  if (!res.ok) throw new Error(String(res.status));
  setRules(await res.json());
  } catch {
  setRulesError(true);
  }
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);

  // The estimate for the answers so far (the server checks only what is present). null → the check failed.
  const askEstimate = async (next) => {
  setCheckError('');
  setChecking(true);
  try {
  const res = await fetch(`${API}/api/uw-matrix/public/quick-check/estimate`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(next),
  });
  if (!res.ok) throw new Error(String(res.status));
  return await res.json();
  } catch {
  setCheckError("We couldn't check that just now. Please try again.");
  return null;
  } finally {
  setChecking(false);
  }
  };

  // ── Confetti burst when eligible screen mounts ───────────────────────────
  useEffect(() => {
  if (screen !== 'eligible') return;

  // Two-cannon burst from bottom corners
  const fire = (originX, angle) =>
  confetti({
  particleCount: 80,
  spread: 70,
  angle,
  origin: { x: originX, y: 1 },
  colors: CONFETTI_COLORS,
  gravity: 0.8,
  scalar: 1.1,
  drift: 0,
  });

  fire(0.2, 60);
  fire(0.8, 120);

  // Second burst after a short delay for drama
  const t = setTimeout(() => {
  fire(0.3, 70);
  fire(0.7, 110);
  }, 350);

  return () => clearTimeout(t);
  }, [screen]);

  // ── Progress % ───────────────────────────────────────────────────────────
  const currentQIndex = QUESTION_SCREENS.indexOf(screen);
  const progressPct = currentQIndex >= 0
  ? Math.round(((currentQIndex + 1) / QUESTION_SCREENS.length) * 100)
  : 0;

  // ── Screen transition ─────────────────────────────────────────────────────
  const goTo = (next, dir = 'forward') => {
  setDirection(dir);
  setAnimating(true);
  setTimeout(() => {
  setScreen(next);
  setAnimating(false);
  if (dir === 'forward') setHistory((h) => [...h, next]);
  window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 200);
  };

  const goBack = () => {
  if (history.length <= 1) return;
  const prev = history[history.length - 2];
  setHistory((h) => h.slice(0, -1));
  goTo(prev, 'back');
  };

  const saveAndGo = (key, value, next) => {
  setAnswers((a) => ({ ...a, [key]: value }));
  goTo(next);
  };

  const reject = (r) => {
  setRejection({ heading: r.heading, body: r.body });
  goTo('rejected');
  };

  // Save the answer, ask the server, then reject (its copy) or move on.
  const answerAndCheck = async (key, value, next) => {
  const merged = { ...answers, [key]: value };
  setAnswers(merged);
  const r = await askEstimate(merged);
  if (!r) return;
  if (r.outcome === 'not_a_fit') { reject(r); return; }
  if (r.estimate) setEstimate(r.lines ? { ...r.estimate, lines: r.lines } : r.estimate);
  goTo(next);
  };

  // ── Gate handlers ─────────────────────────────────────────────────────────
  const handleState = () => {
  if (!stateSelection) { setStateError('Please select a state.'); return; }
  setStateError('');
  setAnswers((a) => ({ ...a, state: stateSelection }));
  if (!(rules && rules.states.includes(stateSelection))) {
  const stateName = US_STATES.find(([abbr]) => abbr === stateSelection)?.[1] || stateSelection;
  setWaitlistForm({ firstName: '', email: '', phone: '' });
  setWaitlistErrors({});
  setWaitlistSuccess(false);
  setWaitlistApiError('');
  setWaitlistModal({ state: stateSelection, stateName });
  return;
  }
  goTo('q2_primary_residence');
  };

  const closeWaitlistModal = () => {
  setWaitlistModal(null);
  setStateSelection(''); // reset dropdown so they can't proceed
  };

  const submitWaitlist = async () => {
  const errs = {};
  if (!waitlistForm.firstName.trim()) errs.firstName = 'First name is required.';
  if (!waitlistForm.email.trim()) {
  errs.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistForm.email)) {
  errs.email = 'Please enter a valid email address.';
  }
  if (Object.keys(errs).length > 0) { setWaitlistErrors(errs); return; }

  setWaitlistLoading(true);
  setWaitlistApiError('');
  try {
  const res = await fetch(`${API}/api/waitlist`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
  first_name: waitlistForm.firstName.trim(),
  email: waitlistForm.email.trim(),
  phone: waitlistForm.phone.trim() || null,
  state: waitlistModal.state,
  state_name: waitlistModal.stateName,
  }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed');
  setWaitlistSuccess(true);
  } catch {
  setWaitlistApiError('Something went wrong. Please try again.');
  } finally {
  setWaitlistLoading(false);
  }
  };

  const handleOccupancy = (v) => answerAndCheck('primary_residence', v, 'q3_property_type');

  const handlePropertyType = (code) => answerAndCheck('property_type', code, 'q4_purchase_price');

  const handlePrice = async () => {
  const num = Number(purchasePrice.replace(/,/g, ''));
  if (!purchasePrice.trim() || isNaN(num) || num <= 0) {
  setPriceError('Please enter a valid purchase price.');
  return;
  }
  const merged = { ...answers, purchase_price: num };
  const r = await askEstimate(merged);
  if (!r) return;
  // 🔑 The price bounds are the matrix's: the server answers price_low / price_high with its own copy.
  if (r.reason === 'price_low' || r.reason === 'price_high') { setPriceRangeModal({ heading: r.heading, body: r.body }); return; }
  if (r.outcome === 'not_a_fit') { setPriceError(r.body || 'Please enter a valid purchase price.'); return; }
  setPriceError('');
  setAnswers(merged);
  goTo('q5_down_payment');
  };

  // The down payment is checked with the credit score (the estimated minimum depends on both), on the next question.
  const handleDownPayment = (code) => saveAndGo('down_payment', code, 'q6_credit_score');

  const handleCredit = (code) => answerAndCheck('credit', code, 'q7_agent');

  // ── Shared UI components ──────────────────────────────────────────────────
  const Choice = ({ label, sublabel, onClick }) => (
  <button
  type="button"
  onClick={onClick}
  className="w-full text-left px-5 py-4 rounded-xl border-2 border-gray-200
  hover:border-doorly-blue hover:bg-blue-50 transition-all duration-150 group"
  >
  <span className="font-medium text-black group-hover:text-doorly-blue transition-colors">
  {label}
  </span>
  {sublabel && (
  <span className="block text-sm text-gray-400 mt-0.5">{sublabel}</span>
  )}
  </button>
  );

  const YesNo = ({ onYes, onNo }) => (
  <div className="flex flex-col sm:flex-row gap-3">
  <button type="button" onClick={onYes}
  className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-200
  hover:border-doorly-blue hover:bg-blue-50 font-medium text-black
  hover:text-doorly-blue transition-all duration-150">
  Yes
  </button>
  <button type="button" onClick={onNo}
  className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-200
  hover:border-red-300 hover:bg-red-50 font-medium text-black
  hover:text-red-600 transition-all duration-150">
  No
  </button>
  </div>
  );

  const QLabel = ({ n, total = 8 }) => (
  <p className="text-doorly-blue text-sm font-semibold mb-2">
  Question {n} of {total}
  </p>
  );

  // Slide transition
  const transition = animating
  ? direction === 'forward'
  ? 'opacity-0 translate-x-8'
  : 'opacity-0 -translate-x-8'
  : 'opacity-100 translate-x-0';

  // ── Screen renderer ───────────────────────────────────────────────────────
  const renderScreen = () => {
  // ⚠️ No rules, no questions: never a copy of our own to fall back on.
  if (!rules && screen !== 'welcome') {
  return (
  <div className="text-center max-w-lg mx-auto" data-testid="prequal-rules-unavailable">
  <p className="text-gray-600 text-base mb-6">{rulesError ? "We couldn't load this check just now." : 'Loading…'}</p>
  {rulesError && (
  <button type="button" onClick={loadRules} className="doorly-cta mx-auto">
  <span className="doorly-cta-text">Try Again</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </button>
  )}
  </div>
  );
  }
  switch (screen) {

  // Welcome ───────────────────────────────────────────────────────────────
  case 'welcome':
  return (
  <div className="text-center max-w-lg mx-auto">
  <div className="flex justify-center mb-6">
  <img src={`${process.env.PUBLIC_URL}/favicon-prequal.png`} alt="Doorly" style={{ height: '64px', width: 'auto' }} />
  </div>
  <h1 className="text-3xl sm:text-4xl font-bold text-black mb-4 leading-tight">
  See if Doorly is a fit for your home purchase{' '}
  <span className="text-doorly-blue">in 60 seconds or less.</span>
  </h1>
  <p className="text-gray-600 text-lg mb-3">
  This short pre-qualification helps us understand your homebuying plans.
  </p>
  <ul className="text-gray-500 text-sm space-y-2 mb-10 inline-block text-left">
  {/* "No impact to your credit" was ACCURATE here — this step is a self-reported
      questionnaire with no bureau call — but unscoped it read as a claim about
      the whole program, which is false: ReadyCheck runs a hard inquiry. Scoped
      to the step rather than removed. */}
  {['No credit check in this step', 'No documents required', 'No obligation'].map((item) => (
  <li key={item} className="flex items-center gap-2">
  <svg className="w-4 h-4 text-doorly-blue flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
  {item}
  </li>
  ))}
  </ul>
  <div className="flex justify-center">
  <button onClick={() => goTo('q1_state')} className="doorly-cta text-lg px-8 py-4">
  <span className="doorly-cta-text">Start</span>
  <span className="doorly-cta-badge text-lg" aria-hidden="true">→</span>
  </button>
  </div>
  {/* Download badges — web app only; hidden inside the native app */}
  {!isNativeApp() && (
  <div className="flex flex-col items-center" style={{ marginTop: 16 }}>
  <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Access Doorly From Anywhere</p>
  <div className="flex flex-wrap items-center justify-center gap-2">
  <AppStoreBadge />
  <GooglePlayBadge />
  </div>
  </div>
  )}
  </div>
  );

  // Q1 ────────────────────────────────────────────────────────────────────
  case 'q1_state':
  return (
  <div>
  <QLabel n={1} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-2">
  Which state are you buying in?
  </h2>
  <div className="max-w-sm space-y-4 mt-8">
  <div className="relative">
  <select
  // ⚠️ caret-own — this select has its own SVG arrow two lines below; see
  // ApplicationForm.js for the same opt-out and why it exists.
  className={`caret-own w-full h-14 px-4 rounded-xl border-2 text-sm text-black bg-white outline-none transition-all duration-150 appearance-none cursor-pointer focus:ring-2 focus:ring-doorly-blue/30 ${
  stateError ? 'border-red-400' : stateSelection ? 'border-doorly-blue' : 'border-gray-200 hover:border-gray-300'
  }`}
  value={stateSelection}
  onChange={(e) => { setStateSelection(e.target.value); setStateError(''); }}
  >
  <option value="">Select a state...</option>
  {US_STATES.map(([abbr, name]) => (
  <option key={abbr} value={abbr}>{name}</option>
  ))}
  </select>
  <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
  </div>
  {stateError && (
  <p className="text-red-500 text-sm flex items-center gap-1">
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
  {stateError}
  </p>
  )}
  <button type="button" onClick={handleState} className="doorly-cta">
  <span className="doorly-cta-text">Continue</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </button>
  </div>
  </div>
  );

  // Q2 ────────────────────────────────────────────────────────────────────
  case 'q2_primary_residence':
  return (
  <div>
  <QLabel n={2} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-8">
  Will this be your primary residence{' '}
  <span className="text-doorly-blue">(owner-occupied)?</span>
  </h2>
  <YesNo onYes={() => handleOccupancy('yes')} onNo={() => handleOccupancy('no')} />
  </div>
  );

  // Q3 ────────────────────────────────────────────────────────────────────
  case 'q3_property_type':
  return (
  <div>
  <QLabel n={3} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-8">
  What type of property are you buying?
  </h2>
  <div className="flex flex-col gap-3">
  {(rules ? rules.propertyTypes : []).map((t) => (
  <Choice key={t.code} label={t.label} sublabel={t.sublabel} onClick={() => handlePropertyType(t.code)} />
  ))}
  </div>
  </div>
  );

  // Q4 ────────────────────────────────────────────────────────────────────
  case 'q4_purchase_price':
  return (
  <div>
  <QLabel n={4} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-2">
  What is your target purchase price?
  </h2>
  <p className="text-gray-500 text-sm mb-8">Please enter the amount in USD.</p>
  <div className="relative max-w-sm">
  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">$</span>
  <input
  type="number"
  className={`doorly-input pl-9 text-lg py-4 ${priceError ? 'doorly-input-error' : ''}`}
  placeholder="350,000"
  value={purchasePrice}
  onChange={(e) => { setPurchasePrice(e.target.value); setPriceError(''); }}
  onKeyDown={(e) => e.key === 'Enter' && handlePrice()}
  autoFocus
  min="1"
  />
  </div>
  {priceError && <p className="text-red-500 text-sm mt-2">{priceError}</p>}
  <button type="button" onClick={handlePrice} className="doorly-cta mt-6">
  <span className="doorly-cta-text">Continue</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </button>
  </div>
  );

  // Q5 ────────────────────────────────────────────────────────────────────
  case 'q5_down_payment':
  return (
  <div>
  <QLabel n={5} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-8">
  How much do you expect to put down?
  </h2>
  <div className="flex flex-col gap-3">
  {(rules ? rules.downPaymentOptions : []).map((o) => (
  <Choice key={o.code} label={o.label} onClick={() => handleDownPayment(o.code)} />
  ))}
  </div>
  </div>
  );

  // Q6 ────────────────────────────────────────────────────────────────────
  case 'q6_credit_score':
  return (
  <div>
  <QLabel n={6} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-8">
  What is your estimated credit score?
  </h2>
  <div className="flex flex-col gap-3">
  {(rules ? rules.creditOptions : []).map((o) => (
  <Choice key={o.code} label={o.label} onClick={() => handleCredit(o.code)} />
  ))}
  </div>
  </div>
  );

  // Q7 ────────────────────────────────────────────────────────────────────
  case 'q7_agent':
  return (
  <div>
  <QLabel n={7} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-8">
  Are you currently working with a real estate agent?
  </h2>
  <YesNo
  onYes={() => { setAnswers((a) => ({ ...a, agent: 'yes' })); setAgentPopup('yes'); }}
  onNo ={() => { setAnswers((a) => ({ ...a, agent: 'no' })); setAgentPopup('no'); }}
  />
  </div>
  );

  // Q8 ────────────────────────────────────────────────────────────────────
  case 'q8_stage':
  return (
  <div>
  <QLabel n={8} />
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-8">
  Where are you in the home-buying process?
  </h2>
  <div className="flex flex-col gap-3">
  <Choice label="Just exploring" onClick={() => saveAndGo('stage', 'exploring', 'eligible')} />
  <Choice label="Actively shopping" onClick={() => saveAndGo('stage', 'shopping', 'eligible')} />
  <Choice label="Have a home picked out" onClick={() => { setAnswers((a) => ({ ...a, stage: 'home_picked' })); setStageModal('home_picked'); }} />
  <Choice label="Under contract" onClick={() => { setAnswers((a) => ({ ...a, stage: 'under_contract' })); setStageModal('under_contract'); }} />
  </div>
  </div>
  );

  // ── Eligible ────────────────────────────────────────────────────────────
  case 'eligible':
  return (
  <div className="text-center max-w-lg mx-auto">
  {/* Trophy / star icon */}
  <div className="inline-flex items-center justify-center w-24 h-24 bg-doorly-yellow rounded-full mb-6 shadow-lg">
  <svg className="w-12 h-12 text-black" fill="currentColor" viewBox="0 0 24 24">
  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
  </div>
  <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 leading-tight">
  Congrats, you may be a great fit for Doorly!
  </h2>
  <p className="text-gray-600 text-lg mb-8 leading-relaxed">
  {/* 🔑 Estimate wording, no approval language (Jesse, 2026-10-29). Was "you look like a strong candidate… get officially approved". */}
  This is an early check, not an approval. Your final eligibility is set once we've reviewed your credit and income.
  Take the next step and submit your full application.
  </p>
  {/* 🔑 The estimate lines are the server's (constants/quickCheckRules estimateLines): estimate wording, no approval. */}
  {estimate && Array.isArray(estimate.lines) && (
  <div data-testid="prequal-estimate" className="text-left bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 mb-8 space-y-1.5">
  {estimate.lines.map((l) => <p key={l} className="text-gray-600 text-sm leading-relaxed">{l}</p>)}
  </div>
  )}
  <button onClick={() => {
    // answers.stage from q8 is one of: 'exploring' | 'shopping' |
    // 'home_picked' | 'under_contract'. Convert to the human-readable
    // canonical value the DB stores (matches BUYING_STAGE_MAP keys on
    // the admin pipeline table) before forwarding to /register.
    const STAGE_CODE_TO_LABEL = {
      exploring: 'Just exploring',
      shopping: 'Actively shopping',
      home_picked: 'Have a home picked out',
      under_contract: 'Under contract',
    };
    const buyerStage = STAGE_CODE_TO_LABEL[answers.stage] || '';
    // ── 🚨 THE GOOGLE ADS CONVERSION FIRES HERE, AND ONLY HERE ─────────────
    //
    // It used to fire on the borrower DASHBOARD, reading loan_stage to decide
    // — an authenticated page keyed to one person's loan, which is now the one
    // kind of page the tag does not load on. This is the last public step
    // before /register, so it is the last point at which a browser tag can
    // honestly report anything.
    //
    // ⚠️ NONE OF `answers` GOES WITH IT. fireConversion takes a name and sends
    // send_to — there is no parameter for a purchase price, a credit score or
    // a state, which is deliberate: those are the next four lines of this very
    // call, and the conversion must not carry them.
    //
    // 🔑 ONCE PER DEVICE, on the key the dashboard used, so a borrower who
    // already converted there does not convert again here.
    if (!localStorage.getItem('gads_conversion_fired')) {
      if (fireConversion('borrower_application')) {
        localStorage.setItem('gads_conversion_fired', 'true');
      }
    }
    navigate('/register', { state: { fromPreQual: true, prefill: { purchasePrice: answers.purchase_price || '', downPaymentRange: answers.down_payment || '', creditScore: legacyCreditCode(answers.credit), buyingState: answers.state || '', buyerStage } } });
  }} className="doorly-cta doorly-cta-yellow text-lg px-8 py-4 mx-auto">
  <span className="doorly-cta-text">Apply Here</span>
  <span className="doorly-cta-badge text-lg" aria-hidden="true">→</span>
  </button>
  <div className="mt-6">
  <Link to="/" className="text-sm text-gray-400 hover:text-black transition-colors">
  Back to Home
  </Link>
  </div>
  </div>
  );

  // ── Rejected ────────────────────────────────────────────────────────────
  case 'rejected': {
  const reason = rejection || { heading: '', body: '' };
  return (
  <div className="text-center max-w-lg mx-auto">
  <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  </div>
  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">{reason.heading}</h2>
  <p className="text-gray-600 text-base mb-4 leading-relaxed">{reason.body}</p>
  <p className="text-gray-400 text-sm mb-8">
  Questions?{' '}
  <a href="mailto:hello@godoorly.com" className="text-doorly-blue font-medium hover:underline">
  hello@godoorly.com
  </a>
  </p>
  <div className="flex flex-col sm:flex-row gap-3 justify-center">
  <button
  onClick={() => { setScreen('welcome'); setHistory(['welcome']); setAnswers({}); setRejection(null); setEstimate(null); }}
  className="doorly-cta doorly-cta-outline text-base px-6 py-3"
  >
  <span className="doorly-cta-badge" aria-hidden="true">←</span>
  <span className="doorly-cta-text">Start Over</span>
  </button>
  <Link to="/" className="doorly-cta doorly-cta-outline text-base px-6 py-3">
  <span className="doorly-cta-text">Back to Home</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </Link>
  </div>
  </div>
  );
  }

  default:
  return null;
  }
  };

  const showChrome = !['welcome', 'eligible', 'rejected'].includes(screen);

  return (
  <div className="min-h-screen bg-white flex flex-col">

  {/* Top bar. The bar is fixed at top:0, so on an inset device (notch /
      status bar) its contents sit UNDER the status bar unless the bar
      itself carries the top inset. Same contract as the AppShell topbar
      and the marketing Navbar: the header owns the inset so its white
      background fills the status-bar strip, rather than body padding
      pushing everything down and exposing a gap above it. See the
      safe-area notes in index.css. */}
  <header
  className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100"
  style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
  >
  <div className="max-w-3xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
  <Link to="/" className="flex items-center gap-2 group">
  <img src={`${process.env.PUBLIC_URL}/doorly_logo_black_icon.png`} alt="Doorly" style={{ height: '32px', width: 'auto' }} />
  </Link>

  {showChrome && (
  <div className="flex items-center gap-3 flex-1 mx-6">
  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
  <div
  className="h-full bg-doorly-blue rounded-full transition-all duration-500"
  style={{ width: `${progressPct}%` }}
  />
  </div>
  <span className="text-xs text-gray-400 w-8 text-right">{progressPct}%</span>
  </div>
  )}

  {/* Exit / X — top-right escape hatch back to the landing page. The
      Doorly logo on the left already navigates to / via <Link to="/">,
      but users on iOS especially weren't connecting that affordance,
      and the only other option was Force Quit + relaunch. iOS shows an
      'X' glyph (native convention — close); web shows the word 'Exit'
      so the action is unambiguous outside the iconography contract.
      Always rendered (no showChrome gate) so the escape works on the
      welcome / eligible / rejected screens too. */}
  <button
  type="button"
  onClick={() => navigate('/')}
  aria-label="Exit pre-qualification"
  className="ml-3 flex items-center justify-center w-9 h-9 text-gray-400 hover:text-black transition-colors"
  >
  {(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) ? (
  // iOS X glyph — native close convention; stroke-only so it stays
  // legible at the small chrome size.
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
  </svg>
  ) : (
  <span className="text-sm font-semibold">Exit</span>
  )}
  </button>
  </div>
  </header>

  {/* Content */}
  {/* pt grows with the header: 6rem (the old pt-24) clears the 4rem bar
      plus breathing room, and the inset is added on top because the
      fixed header is now that much taller. */}
  <main
  className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-16"
  style={{ paddingTop: 'calc(6rem + env(safe-area-inset-top, 0px))' }}
  >
  <div className="w-full max-w-2xl" ref={containerRef}>
  <div className={`transition-all duration-200 ease-out ${transition}`}>
  {renderScreen()}
  </div>

  {showChrome && (checkError || checking) && (
  <p className={`mt-6 text-sm ${checkError ? 'text-red-500' : 'text-gray-400'}`} role="status">{checkError || 'Checking…'}</p>
  )}

  {showChrome && history.length > 1 && (
  <button
  onClick={goBack}
  className="mt-10 flex items-center gap-1.5 text-sm text-gray-400 hover:text-black transition-colors"
  >
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
  Back
  </button>
  )}
  </div>
  </main>

  <div className="text-center pb-6 px-4">
  <p className="text-gray-400 text-xs">Doorly | NMLS #2764749 | Equal Housing Opportunity</p>
  </div>

  {/* ── Purchase price out of range modal ──────────────────────────────── */}
  {priceRangeModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
  <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-5">
  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
  </div>
  <h3 className="text-xl font-bold text-black mb-3">
  {priceRangeModal.heading}
  </h3>
  <p className="text-gray-600 text-sm leading-relaxed mb-6">
  {priceRangeModal.body}
  </p>
  <button
  type="button"
  onClick={() => { setPriceRangeModal(null); setPriceError(''); }}
  className="doorly-cta w-full justify-center"
  style={{ backgroundColor: '#3a6ad9' }}
  >
  <span className="doorly-cta-badge" aria-hidden="true">←</span>
  <span className="doorly-cta-text">OK, Update My Answer</span>
  </button>
  </div>
  </div>
  )}

  {/* ── Stage info pop-up (home picked / under contract) ───────────────── */}
  {stageModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
  <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5 ${stageModal === 'under_contract' ? 'bg-green-50' : 'bg-blue-50'}`}>
  {stageModal === 'under_contract' ? (
  <svg className="w-7 h-7 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  ) : (
  <svg className="w-7 h-7 text-doorly-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
  )}
  </div>
  <h3 className="text-xl font-bold text-black mb-3">
  {stageModal === 'under_contract' ? 'Fantastic!' : 'Nice!'}
  </h3>
  <p className="text-gray-600 text-sm leading-relaxed">
  {stageModal === 'home_picked'
  ? <>If you're approved, Doorly will handle submitting the offer and purchasing the home on your behalf. To keep everything smooth and avoid conflicts, <strong>please don't submit an offer yourself.</strong> We'll take care of that step for you inside the app.</>
  : <>If you're approved, Doorly will step in as the buyer and guide you through transitioning your current contract.<br /><br />We'll provide step-by-step instructions to help you move forward smoothly.</>
  }
  </p>
  <button
  type="button"
  onClick={() => { setStageModal(null); goTo('eligible'); }}
  className="doorly-cta mt-6 w-full justify-center"
  style={{ backgroundColor: '#3a6ad9' }}
  >
  <span className="doorly-cta-text">Got It Continue</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </button>
  </div>
  </div>
  )}

  {/* ── Waitlist modal ──────────────────────────────────────────────────── */}
  {waitlistModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative">

  {/* Close button */}
  <button
  onClick={closeWaitlistModal}
  className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
  aria-label="Close"
  >
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
  </button>

  {waitlistSuccess ? (
  /* ── Success state ── */
  <div className="text-center py-4">
  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ backgroundColor: '#eff3fe' }}>
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3a6ad9' }}>
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
  </div>
  <h3 className="text-xl font-bold text-gray-900 mb-3">You're on the list!</h3>
  <p className="text-gray-600 text-sm leading-relaxed mb-6">
  We'll reach out as soon as Doorly launches in <strong>{waitlistModal.stateName}</strong>. Keep an eye on your inbox.
  </p>
  <button
  onClick={closeWaitlistModal}
  className="btn-primary btn-full text-gray-900 hover:opacity-90"
  style={{ backgroundColor: '#fffd5b' }}
  >
  Close
  </button>
  </div>
  ) : (
  /* ── Form state ── */
  <>
  {/* Icon */}
  <div className="flex justify-center mb-5">
  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl" style={{ backgroundColor: '#eff3fe' }}>
  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3a6ad9' }}>
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
  </div>
  </div>

  <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
  Doorly is coming to {waitlistModal.stateName} soon!
  </h3>
  <p className="text-gray-500 text-sm text-center leading-relaxed mb-6">
  We are not available in {waitlistModal.stateName} yet but we are expanding fast.
  Join our waitlist and we'll notify you the moment we launch in your area.
  </p>

  {waitlistApiError && (
  <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
  {waitlistApiError}
  </div>
  )}

  <div className="space-y-3">
  {/* First Name */}
  <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
  First Name <span className="text-red-400">*</span>
  </label>
  <input
  type="text" autoComplete="given-name"
  value={waitlistForm.firstName}
  onChange={e => { setWaitlistForm(f => ({ ...f, firstName: e.target.value })); setWaitlistErrors(er => ({ ...er, firstName: undefined })); }}
  placeholder="John"
  className={`w-full h-12 px-4 rounded-xl border text-sm focus:outline-none focus:ring-2 ${waitlistErrors.firstName ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 focus:ring-blue-100 focus:border-blue-400'}`}
  />
  {waitlistErrors.firstName && <p className="text-xs text-red-500 mt-1">{waitlistErrors.firstName}</p>}
  </div>

  {/* Email */}
  <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
  Email Address <span className="text-red-400">*</span>
  </label>
  <input
  type="email" autoComplete="email"
  value={waitlistForm.email}
  onChange={e => { setWaitlistForm(f => ({ ...f, email: e.target.value })); setWaitlistErrors(er => ({ ...er, email: undefined })); }}
  placeholder="john@example.com"
  className={`w-full h-12 px-4 rounded-xl border text-sm focus:outline-none focus:ring-2 ${waitlistErrors.email ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 focus:ring-blue-100 focus:border-blue-400'}`}
  />
  {waitlistErrors.email && <p className="text-xs text-red-500 mt-1">{waitlistErrors.email}</p>}
  </div>

  {/* Phone (optional) */}
  <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
  Phone Number <span className="text-gray-400 font-normal">(optional)</span>
  </label>
  <input
  type="tel" autoComplete="tel"
  value={waitlistForm.phone}
  onChange={e => setWaitlistForm(f => ({ ...f, phone: e.target.value }))}
  placeholder="(555) 555-5555"
  className="w-full h-12 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
  />
  </div>

  {/* Submit */}
  <button
  type="button"
  onClick={submitWaitlist}
  disabled={waitlistLoading}
  className="btn-primary btn-full text-gray-900 gap-2 hover:opacity-90 disabled:opacity-70 mt-1"
  style={{ backgroundColor: '#fffd5b' }}
  >
  {waitlistLoading ? (
  <>
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
  Joining…
  </>
  ) : (
  <>
  <span>Join the Waitlist</span>
  <span className="btn-arrow" style={{ backgroundColor: '#3a6ad9' }}>
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
  </span>
  </>
  )}
  </button>

  <p className="text-center text-xs text-gray-400 pt-1">
  No spam. We'll only contact you when we launch in {waitlistModal.stateName}.
  </p>
  </div>
  </>
  )}
  </div>
  </div>
  )}

  {/* ── Agent answer pop-up ─────────────────────────────────────────────── */}
  {agentPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
  <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">

  {/* Icon */}
  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5 ${agentPopup === 'no' ? 'bg-yellow-50' : 'bg-blue-50'}`}>
  {agentPopup === 'no' ? (
  <svg className="w-7 h-7 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
  ) : (
  <svg className="w-7 h-7 text-doorly-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
  )}
  </div>

  {/* Copy */}
  {agentPopup === 'no' ? (
  <>
  <h3 className="text-xl font-bold text-black mb-3">No realtor? No problem.</h3>
  <p className="text-gray-600 text-sm leading-relaxed">
  Doorly handles everything for you approval, home selection, financing, and closing all in one seamless app. No middlemen, no delays, just a faster path to getting your keys.
  </p>
  </>
  ) : (
  <>
  <h3 className="text-xl font-bold text-black mb-3">Already working with a realtor? That's okay.</h3>
  <p className="text-gray-600 text-sm leading-relaxed">
  Doorly will take the lead from here to keep your process fast and streamlined. Your realtor can still stay involved through DoorlyPro you can invite them during your application if they're not already a partner.
  </p>
  </>
  )}

  {/* CTA */}
  <button
  type="button"
  onClick={() => { setAgentPopup(null); goTo('q8_stage'); }}
  className="doorly-cta mt-6 w-full justify-center"
  style={{ backgroundColor: '#3a6ad9' }}
  >
  <span className="doorly-cta-text">Continue</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </button>
  </div>
  </div>
  )}
  </div>
  );
}

export default PreQualification;
